import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { NavBar } from 'components/NavBar/NavBar'
import {
  Button,
  Flex,
  MainHeadline,
  PageWrapper,
} from 'styling/GlobalStyles'
import {
  BodyText,
  Heading3,
  OtherText,
  ParagraphText,
} from 'styling/Typography'
import {
  HeaderRow,
  HeaderActions,
  KpiGrid,
  KpiCard,
  KpiValue,
  DashGrid,
  Panel,
  PanelHeader,
  CompositionGrid,
  CompositionRow,
  CostBarRow,
  LinearTrack,
  LinearFill,
  Divider,
  TotalRow,
  UtilRow,
  UtilBar,
  UtilFill,
  UtilMark,
  AlertsList,
  AlertItem,
  SeverityPill,
  AlertCount,
  fleetColors,
} from './FleetOverview.styled'

const TruckIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
    <path d='M3 7h11v8H3z' />
    <path d='M14 10h4l3 3v2h-7z' />
    <circle cx='7' cy='17' r='2' />
    <circle cx='17' cy='17' r='2' />
  </svg>
)

const WrenchIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M14.7 6.3a4 4 0 00-5.4 5.4l-6 6a2 2 0 102.8 2.8l6-6a4 4 0 005.4-5.4l-2.5 2.5-2.3-2.3 2.5-2.5z' />
  </svg>
)

const FuelIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M4 21h9V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16z' />
    <path d='M13 9h3l2 2v7a2 2 0 01-2 2' />
    <path d='M7 9h3' />
  </svg>
)

const PickupIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
    <path d='M2 8h10v9H2z' />
    <path d='M12 11h5l4 3v3h-9z' />
    <circle cx='6' cy='18' r='1.7' />
    <circle cx='17' cy='18' r='1.7' />
  </svg>
)

const TrendIcon = ({ color }: { color: string }) => (
  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth={2.5} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M3 17l6-6 4 4 8-8' />
    <path d='M14 7h7v7' />
  </svg>
)

const composition = [
  { id: 'HT', name: 'Haul Truck', type: 'Heavy Equipment', count: 18, icon: <TruckIcon /> },
  { id: 'EX', name: 'Excavator', type: 'Heavy Equipment', count: 8, icon: <WrenchIcon /> },
  { id: 'WL', name: 'Wheel Loader', type: 'Heavy Equipment', count: 6, icon: <WrenchIcon /> },
  { id: 'BD', name: 'Bulldozer', type: 'Heavy Equipment', count: 5, icon: <WrenchIcon /> },
  { id: 'MG', name: 'Motor Grader', type: 'Heavy Equipment', count: 3, icon: <WrenchIcon /> },
  { id: 'FT', name: 'Fuel Truck', type: 'Support', count: 4, icon: <FuelIcon /> },
  { id: 'WT', name: 'Water Truck', type: 'Support', count: 3, icon: <TruckIcon /> },
  { id: 'PU', name: 'Pickup', type: 'Light Vehicle', count: 12, icon: <PickupIcon /> },
]

const alerts: Array<{
  severity: 'critical' | 'high' | 'medium' | 'low'
  title: string
  ref: string
  time: string
}> = [
  { severity: 'critical', title: 'HT-11 transmission fault-out of service', ref: 'WO-2043', time: '8min ago' },
  { severity: 'high', title: 'EX-02 hydraulic leak detected', ref: 'WO-2042', time: '1h ago' },
  { severity: 'medium', title: 'FT-02 awaiting pump motor parts (procurement)', ref: 'WO-2043', time: '4d ago' },
  { severity: 'medium', title: '4 machines below 25% fuel-refuel suggested', ref: '', time: 'now' },
  { severity: 'low', title: 'WL-03 service due in 12 operating hours', ref: '', time: 'today' },
]

export const FleetOverview: React.FC = () => {
  const navigate = useNavigate()
  const theme: any = useTheme()

  const costBreakdown = [
    { label: 'Fuel', amount: '$84.2k', pct: 42, color: theme.colorOrange },
    { label: 'Maintenance & Parts', amount: '$51.8k', pct: 26, color: fleetColors.yellow },
    { label: 'Operator Labor', amount: '$39.6k', pct: 20, color: fleetColors.info },
    { label: 'Depreciation', amount: '$24.6k', pct: 12, color: fleetColors.purple },
  ]

  const utilization = [
    { name: 'Haul Truck', value: 78, target: 75, color: fleetColors.success },
    { name: 'Excavator', value: 71, target: 80, color: theme.colorOrange },
    { name: 'Wheel Loader', value: 65, target: 70, color: theme.colorOrange },
    { name: 'Buldozer', value: 58, target: 85, color: theme.colorOrange },
    { name: 'Fuel Truck', value: 42, target: 50, color: theme.colorOrange },
    { name: 'Pickup', value: 35, target: 40, color: theme.colorOrange },
  ]

  const kpiCards = [
    { label: 'Availability', value: '55%', sub: '44 of 80 operational', accent: fleetColors.success, trend: true },
    { label: 'Avg Utilization (24h)', value: '59%', sub: 'vs target 70%', accent: theme.colorOrange },
    { label: 'MTBF (Fleet Avg)', value: '497h', sub: 'last 30 days', accent: theme.textColor, trend: true },
    { label: 'Cost per Op-Hour', value: '$182', sub: 'fleet weighted avg', accent: theme.textColor },
  ]

  const statCards = [
    { label: 'In Use Now', value: '32', sub: '12 available, 16 down', accent: theme.colorOrange },
    { label: 'Under Maintenance', value: '8', sub: '3 WOs in progress', accent: theme.textColor },
    { label: 'Broken/Critical', value: '8', sub: '1 critical WO open', accent: fleetColors.danger },
    { label: 'Low Fuel', value: '19', sub: 'machines below 25%', accent: fleetColors.warning },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <HeaderRow>
          <MainHeadline>Fleet Overview</MainHeadline>
          <HeaderActions>
            <Button orange>All Sites</Button>
            <Button orange>Live Map</Button>
          </HeaderActions>
        </HeaderRow>

        <KpiGrid>
          {kpiCards.map((k) => (
            <KpiCard key={k.label} accent={k.accent}>
              <ParagraphText grey weight='600' style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {k.label}
              </ParagraphText>
              <KpiValue accent={k.accent}>{k.value}</KpiValue>
              <Flex gap='0.35rem' center>
                {k.trend && <TrendIcon color={k.accent} />}
                <OtherText grey>{k.sub}</OtherText>
              </Flex>
            </KpiCard>
          ))}
        </KpiGrid>

        <KpiGrid>
          {statCards.map((k) => (
            <KpiCard key={k.label} accent={k.accent}>
              <ParagraphText grey weight='600' style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {k.label}
              </ParagraphText>
              <KpiValue accent={k.accent} size={28}>{k.value}</KpiValue>
              <OtherText grey>{k.sub}</OtherText>
            </KpiCard>
          ))}
        </KpiGrid>

        <DashGrid>
          <Panel>
            <PanelHeader>
              <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Fleet Composition</Heading3>
              <span className='view-all' onClick={() => navigate('/machinery/categories')}>
                View all ›
              </span>
            </PanelHeader>
            <CompositionGrid>
              {composition.map((c) => (
                <CompositionRow key={c.id}>
                  <div className='left'>
                    <div className='icon-wrap'>{c.icon}</div>
                    <div>
                      <BodyText weight='700' style={{ margin: 0 }}>{c.name}</BodyText>
                      <OtherText grey style={{ margin: 0 }}>{c.type}</OtherText>
                    </div>
                  </div>
                  <span className='count'>{c.count}</span>
                </CompositionRow>
              ))}
            </CompositionGrid>
          </Panel>

          <Panel>
            <PanelHeader>
              <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Cost Breakdown (last 7d)</Heading3>
            </PanelHeader>
            {costBreakdown.map((c) => (
              <CostBarRow key={c.label}>
                <div className='top'>
                  <BodyText style={{ margin: 0 }}>{c.label}</BodyText>
                  <span className='amt'>
                    {c.amount}
                    <span className='pct'>{c.pct}%</span>
                  </span>
                </div>
                <LinearTrack>
                  <LinearFill width={c.pct * 2} color={c.color} />
                </LinearTrack>
              </CostBarRow>
            ))}
            <Divider />
            <TotalRow>
              <BodyText grey style={{ margin: 0 }}>Total fleet cost</BodyText>
              <strong>$200.0k</strong>
            </TotalRow>
          </Panel>
        </DashGrid>

        <DashGrid>
          <Panel>
            <PanelHeader>
              <div>
                <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Utilization by Category</Heading3>
                <OtherText grey style={{ marginTop: 4 }}>Last 24h vs. operational target</OtherText>
              </div>
            </PanelHeader>
            {utilization.map((u) => (
              <UtilRow key={u.name}>
                <div className='label-row'>
                  <BodyText style={{ margin: 0 }}>{u.name}</BodyText>
                  <span className='value' style={{ color: u.color }}>
                    {u.value}%<span className='target'>/ {u.target}%</span>
                  </span>
                </div>
                <UtilBar>
                  <UtilFill width={u.value} color={u.color} />
                  <UtilMark position={u.target} />
                </UtilBar>
              </UtilRow>
            ))}
          </Panel>

          <Panel>
            <PanelHeader>
              <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Active Alerts</Heading3>
              <AlertCount>{alerts.length}</AlertCount>
            </PanelHeader>
            <AlertsList>
              {alerts.map((a, i) => (
                <AlertItem key={i} severity={a.severity}>
                  <div className='header'>
                    <SeverityPill severity={a.severity}>{a.severity}</SeverityPill>
                    <OtherText grey style={{ margin: 0 }}>{a.time}</OtherText>
                  </div>
                  <BodyText weight='600' style={{ margin: 0, lineHeight: 1.3 }}>{a.title}</BodyText>
                  {a.ref && <OtherText grey style={{ marginTop: 4 }}>{a.ref}</OtherText>}
                </AlertItem>
              ))}
            </AlertsList>
          </Panel>
        </DashGrid>
      </PageWrapper>
    </>
  )
}
